<template>
  <div class="w-100 h-100 d-flex flex-column font-size-16px text-dark">
    <div v-if="user" class="position-relative bg-white d-flex flex-column flex-fill rounded-lg w-100 overflow-hidden" :class="{'min-h-500px': conversationMessages.data.length > 0}">
      <div class="h-50px px-4 d-flex flex-shrink-0 align-items-center border-bottom">
        <UserAvatar
          class="w-30px h-30px"
          :display-name="userDisplayName"
          :image-url="userPhotoUrl"
        />
        <span class="mx-4">Chat with {{ isChattingWithAi ? 'AI Assistant' : user.name }}</span>

        <tippy-tooltip
          class="line-height-0"
          :tippy-options="{interactive: true}"
        >
          <i class="fad fa-info-circle" />
          <template #content>
            This is a trained AI assistant to provide customer service and sales support.
            AI can make mistakes.
            Learn more about Dubb Sia <a href="https://dubb.com/features/sia" target="_blank">here</a>.
          </template>
        </tippy-tooltip>
      </div>
      <div ref="messages" class="overflow-auto flex-grow-1">
        <div v-if="loadingConversationMessages" class="text-center"><i class="fal fa-spin fa-spinner"></i></div>
        <ul class="list-unstyled p-0">
          <li v-if="showDefaultMessage">
            <div class="px-4 py-2 tw-flex tw-shrink-0 tw-gap-4">
              <UserAvatar
                class="w-30px h-30px"
                :display-name="userDisplayName"
                :image-url="userPhotoUrl"
              />
              <div class="border shadow-xs w-fit p-3 rounded-lg">
                <div>
                  <p
                    v-for="msg in defaultMessages"
                    class="mb-0"
                  >
                    {{ msg }}
                  </p>
                </div>
              </div>
            </div>
          </li>
          <li v-for="message in conversationMessages.data">
            <div
              :class="{
                'flex-row-reverse': message.sender_id === spark.state.user.id
              }"
              class="px-4 py-2 tw-flex tw-shrink-0 tw-gap-4"
            >
              <UserAvatar
                class="w-30px h-30px"
                :display-name="message.sender_id === spark.state.user.id ? spark.state.user.name : userDisplayName"
                :image-url="message.sender_id === spark.state.user.id ? spark.state.user.photo_url : userPhotoUrl"
              />

              <div
                class="border shadow-xs w-fit p-3 rounded-lg"
              >
                <div v-if="message.is_typing">
                  <p class="px-2 mb-0"><i class="fal fa-ellipsis fa-beat"></i></p>
                </div>
                <div v-else-if="!message.is_rich">
                  <p
                    v-for="msg in message.message.split('\n')"
                    class="mb-0"
                  >
                    {{ msg }}
                  </p>
                </div>
                <div v-else>
                  <div v-html="convert_markdown(message.message)"></div>
                  <div v-if="message.video_canonicals && message.video_canonicals.length > 0">
                    <quick-conversation-video-preview
                      v-for="canonical in message.video_canonicals"
                      :key="canonical"
                      :canonical="canonical"
                    />
                  </div>
                </div>
                <div
                  v-if="message.sender_id !== spark.state.user.id && message.message"
                  class="d-flex align-items-center"
                >
                  <message-audio-button
                    :message="message.message"
                  />
                  <message-copy-button
                    class="ml-2"
                    :message="message.message"
                  />
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div class="px-4 mt-1 position-relative">
        <div
          v-show="!shouldNotShowScrollToBottom"
          class="-top-50px position-absolute left-0 right-0 mx-auto w-100px text-center"
        >
          <button
            class="btn btn-light btn-icon btn-sm btn-circle border"
            @click="scrollToBottom"
          >
            <i class="fal fa-arrow-down" />
          </button>
        </div>

        <quick-conversation-candidate-responses
          @quick-response="quickRespond"
        />
        <div v-if="debugMode && spark.state.user" class="d-flex d-md-block flex-column text-center position-relative mt-2">
          <button
            type="button"
            class="btn btn-danger btn-sm btn-block font-size-16px"
            @click="resetConversation"
          >
            Reset Conversation
          </button>
        </div>
      </div>
      <div class="position-relative flex-shrink-0 px-4 mt-2">
        <input
          v-model="composeForm.draft"
          placeholder="Ask a question"
          class="font-size-16px h-45px h-xl-50px form-control pr-8"
          autocomplete="off"
          @keyup.enter.prevent="checkAndSend"
        />
        <button
          type="button"
          class="btn btn-icon btn-primary btn-sm rounded-circle position-absolute top-0 bottom-0 my-auto right-0 mr-7"
          :disabled="composeForm.busy || composeForm.draft.length <= 0"
          @pointerdown.prevent
          @click="checkAndSend"
        >
          <i class="fal fa-arrow-up"></i>
        </button>
      </div>
    </div>

    <p class="text-center mt-4">
      <a
        :href="siaUrl"
        target="_blank"
        class="text-muted font-size-xs"
      >
        Powered by Dubb SIA
      </a>
    </p>
  </div>
</template>

<script>
import Store from 'store'
import { computed, nextTick } from 'vue'
import _ from 'lodash'
import axios from 'axios'
import URI from 'urijs'
import { DateTime } from 'luxon'
import { pagination, convert_markdown } from '../../utils'
import { fill_utm_form } from '../../common/utm-utils'
import StorageKeys from '../../common/storage-keys'
import { templateRef, useScroll } from '@vueuse/core'
import { useProvideQuickConversation } from './use-quick-conversation'
import QuickConversationCandidateResponses from './quick-conversation-candidate-responses.vue'
import MessageAudioButton from '../common/message-audio-button.vue'
import MessageCopyButton from '../common/message-copy-button.vue'
import quickConversationVideoPreview from './quick-conversation-video-preview.vue'
import TippyTooltip from '../common/tippy-tooltip.vue'
import { bus } from '../../common/setup-app/event-bus.js'
import UserAvatar from '@/components/common/user-avatar.vue'

export default {
  name: 'QuickConversation',
  components: {
    UserAvatar,
    TippyTooltip,
    MessageCopyButton,
    MessageAudioButton,
    QuickConversationCandidateResponses,
    quickConversationVideoPreview
  },
  props: {
    user: {
      required: true
    },
    defaultMessages: {
      type: Array,
      default: () => []
    },
    candidateResponses: {
      type: Array,
      default: () => []
    },
    video: {},
    salesAssistant: {
      required: true
    }
  },
  setup (props) {
    useProvideQuickConversation(props)

    const messagesRef = templateRef('messages')
    const { y, arrivedState } = useScroll(messagesRef)

    const shouldNotShowScrollToBottom = computed(() => {
      return arrivedState.bottom || arrivedState.top
    })

    const scrollToBottom = () => {
      nextTick(() => {
        y.value = messagesRef.value.scrollHeight
      })
    }

    const scrollToLastMessage = () => {
      nextTick(() => {
        const lastMessageHeight = messagesRef.value.querySelector('ul > li:last-child').clientHeight
        y.value = Math.max(messagesRef.value.scrollHeight - lastMessageHeight - 15, 0)
      })
    }

    return {
      shouldNotShowScrollToBottom,
      scrollToBottom,
      scrollToLastMessage
    }
  },
  data () {
    return {
      showDefaultMessage: false,
      defaultMessageTime: null,
      conversationId: null,
      conversationMessages: pagination(),
      loadingConversationMessages: false,
      debugMode: false,
      composeForm: new SparkForm({
        draft: '',
        message: '',
        from_cta: true,
        video_id: this.video ? this.video.id : null,
        hash_sales_assistant_id: this.salesAssistant.hash_id
      }),
      signUpForm: new SparkForm({
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_content: '',
        utm_term: '',
        referrer_url: '',
        landing_url: null,
        context: {
          source: 'chat',
          video_id: this.video ? this.video.id : null
        }
      })
    }
  },
  computed: {
    isChattingWithAi () {
      // TODO use another way to determine if chatting with AI
      return !!this.salesAssistant
    },
    userPhotoUrl () {
      if (this.isChattingWithAi) {
        return 'https://do.dubbcdn.com/img/sia-logo.png'
      }

      return this.user.photo_url
    },
    userDisplayName () {
      return this.isChattingWithAi ? 'AI Assistant' : this.user.name
    },
    siaUrl () {
      let queries = {
        utm_source: 'SiaFeaturePage',
        utm_medium: 'lp',
        utm_campaign: 'Footer',
        utm_content: 'link'
      }
      if (window.affiliateParams) {
        queries = {
          ...queries,
          ...window.affiliateParams
        }
      }

      return URI('https://dubb.com/features/sia').addQuery(queries).toString()
    },
    aiIsTyping () {
      return !!_.find(this.conversationMessages.data, { is_typing: true })
    }
  },
  mounted () {
    const query = URI.parseQuery(location.search)
    if (query.email) {
      this.signUpForm.email = query.email
    }
    if (query.debug_sia) {
      this.enableDebugMode(query.debug_sia)
    }

    if (!Spark.state.user && sessionStorage.getItem(StorageKeys.TEMP_USER_STATE)) {
      this.initSparkState(JSON.parse(sessionStorage.getItem(StorageKeys.TEMP_USER_STATE)))
    }

    this.findConversation()

    bus.$on('private-channel.messaging.new-chat-message', event => {
      if (!event.conversation || !event.message) {
        return
      }
      if (this.conversationId && event.conversation.id === this.conversationId) {
        this.appendMessage(event.message)
      }
    })
    bus.$on('private-channel.messaging.ai-typing-status', event => {
      if (this.conversationId && event.user_id === this.user.id && !this.aiIsTyping) {
        this.appendMessage({ sender_id: this.user.id, is_typing: true })
      }
    })
  },
  methods: {
    convert_markdown,
    findConversation () {
      if (!this.spark.state.user) {
        this.initDefaultMessage()

        return
      }
      axios.get(this.router('api.conversation.find', { friend: this.user.id }))
        .then(resp => {
          if (resp.data) {
            this.conversationId = resp.data.id
            this.loadMessages(1)
          } else {
            this.initDefaultMessage()
          }
        })
    },
    initSparkState (state) {
      Spark.state = { ...Spark.state, ...state }
      bus.$emit('login-user', { apiMode: true })
    },
    enableDebugMode (token) {
      this.debugMode = true
      axios.interceptors.request.use(config => {
        config.headers['X-Debug-Sia'] = token
        return config
      })
      const logMessage = message => {
        switch (message.type) {
          case 'message':
            if (typeof message.content === 'string') {
              console.log(`[${message.role}]${message.content}`)
            } else {
              message.content.forEach(msg => {
                console.log(`[${message.role}]${msg.text}`)
              })
            }
            break
          case 'function_call':
            console.log(`[Function call]${message.name}(${message.arguments})`)
            break
          case 'function_call_output':
            console.log(`[Function call output]${message.output}`)
            break
        }
      }
      bus.$on('private-channel.messaging.ai-debug', event => {
        const headerFormat = 'color: blue; font-weight: bold; font-size: 14px;'
        console.log(
          `%cRequest Model: ${event.payload.request.model}, Enabled Tools: ` + event.payload.request.tools.map(tool => tool.name).join(', '),
          headerFormat
        )
        event.payload.request.input.forEach(message => {
          logMessage(message)
        })
        console.log('%cResponse: ', headerFormat)
        event.payload.response.output.forEach(message => {
          logMessage(message)
        })
      })
    },
    signUp () {
      fill_utm_form(this.signUpForm, {
        utm_source: 'Dubb',
        utm_medium: 'chat',
        utm_campaign: 'DubbChat',
        utm_content: 'SalesAssistant'
      })

      const referrerUrl = Store.get(StorageKeys.REFERRER_URL)

      if (referrerUrl) {
        this.signUpForm.referrer_url = referrerUrl
      } else {
        this.signUpForm.referrer_url = location.href
      }
      const landingUrl = Store.get(StorageKeys.LANDING_URL)
      if (landingUrl) {
        this.signUpForm.landing_url = landingUrl
      } else {
        this.signUpForm.landing_url = location.href
      }

      Spark.post(this.router('api.quick-register'), this.signUpForm)
        .then(resp => {
          bus.$once('pusher-connected', () => {
            this.sendMessage()
          })
          sessionStorage.setItem(StorageKeys.TEMP_USER_STATE, JSON.stringify(resp))
          this.initSparkState(resp)
        })
    },
    checkAndSend () {
      if (this.spark.state.user) {
        this.sendMessage()
        return
      }
      this.signUp()
    },
    initDefaultMessage () {
      this.defaultMessageTime = DateTime.now()
      this.showDefaultMessage = true
    },
    loadMessages (page = 1) {
      if (this.loadingConversationMessages) {
        return
      }
      this.loadingConversationMessages = true
      axios.get(this.router('api.conversation.messages', { conversation: this.conversationId, page }))
        .then(resp => {
          const messages = _.reverse(resp.data.data)
          const videoUrlRegex = new RegExp(`${location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\/v\\/([\\p{L}\\p{M}\\p{N}_-]+)`, 'gu')

          messages.forEach(message => {
            if (message.is_rich) {
              const matches = [...message.message.matchAll(videoUrlRegex)]

              if (matches.length > 0) {
                const canonical = matches.map(match => match[1])

                if (canonical.length > 0) {
                  message.video_canonicals = _.uniq(canonical)
                }
              }
            }
          })

          if (page === 1) {
            this.conversationMessages.data = messages
            if (messages.length === 0 && this.defaultMessage !== '') {
              this.initDefaultMessage()
            }
          } else {
            this.conversationMessages.data = _.concat(messages, this.conversationMessages.data)
          }
          this.conversationMessages.meta = resp.data.meta
          if (page === 1) {
            this.scrollToBottom()
          }
          this.loadingConversationMessages = false
        })
    },
    quickRespond (msg) {
      this.composeForm.draft = msg
      this.checkAndSend()
    },
    sendMessage () {
      if (this.spark.state.user.id === this.user.id) {
        window.SwalConfirm.fire({
          icon: 'warning',
          title: 'You cannot chat with yourself',
          text: 'Copy and paste this URL into an incognito browser window to test your AI chat assistant.',
          showCancelButton: false
        })
        return
      }

      this.composeForm.message = _.trim(this.composeForm.draft)
      if (this.composeForm.message === '') {
        return
      }
      this.composeForm.draft = ''
      Spark.post(this.router('api.conversation.send', { friend: this.user.id }), this.composeForm)
        .then(resp => {
          this.composeForm.message = ''
          this.composeForm.draft = ''
          this.appendMessage(resp)
          this.conversationId = resp.conversation_id
        })
    },
    appendMessage (message) {
      const index = _.findIndex(this.conversationMessages.data, { id: message.id })
      if (index >= 0) {
        this.conversationMessages.data[index] = message
      } else {
        this.conversationMessages.data.push(message)
      }
      if (message.sender_id !== this.spark.state.user.id) {
        this.readConversation()

        if (!message.is_typing) {
          this.conversationMessages.data = _.filter(this.conversationMessages.data, message => !message.is_typing)
        }
      }
      this.scrollToLastMessage()
    },
    resetConversation () {
      axios.post(this.router('api.conversation.reset', { friend: this.user.id }))
        .then(() => {
          location.reload()
        })
    },
    readConversation () {
      if (this.currentConversation) {
        axios.post(this.router('api.conversation.read', { conversation: this.conversationId }))
      }
    }
  }
}
</script>

<style scoped>
.-top-50px {
  top: -50px;
}

</style>
